import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

/**
 * Android/Chrome/Edge Install App prompt.
 * Uses the beforeinstallprompt event (Chromium browsers).
 * Hides on iOS (IosPushHelp handles that path) and when already installed.
 */
export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Skip on iOS (has its own helper) and if already installed
    const ua = navigator.userAgent || '';
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    if (isIos) return;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (isStandalone) return;

    // Respect user dismissal for 14 days
    const dismissed = localStorage.getItem('ptt_install_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed, 10) < 14 * 24 * 60 * 60 * 1000) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    const onInstalled = () => {
      setShow(false);
      setDeferredPrompt(null);
      localStorage.setItem('ptt_install_dismissed', String(Date.now()));
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (!show || !deferredPrompt) return null;

  const install = async () => {
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === 'dismissed') {
        localStorage.setItem('ptt_install_dismissed', String(Date.now()));
      }
    } catch { /* noop */ }
    setDeferredPrompt(null);
    setShow(false);
  };

  const dismiss = () => {
    localStorage.setItem('ptt_install_dismissed', String(Date.now()));
    setShow(false);
  };

  return (
    <div className="fixed inset-x-3 bottom-3 sm:right-4 sm:left-auto sm:max-w-sm z-50 bg-white rounded-2xl shadow-2xl border border-orange-200 overflow-hidden" data-testid="install-prompt">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <img src="/brand/icon-192.png" alt="PTT NZ" className="w-11 h-11 rounded-xl flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-sm">Install PTT NZ Alerts</div>
            <div className="text-xs text-zinc-600 mt-0.5">Add to your device for instant road-alert push notifications, even when the browser is closed.</div>
          </div>
          <button onClick={dismiss} className="text-zinc-400 hover:text-zinc-700 flex-shrink-0 -mr-1" data-testid="install-dismiss" aria-label="Dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={install}
            className="btn btn-primary flex-1"
            data-testid="install-accept"
          >
            <Download className="w-4 h-4" /> Install app
          </button>
          <button onClick={dismiss} className="btn btn-ghost" data-testid="install-later">
            Later
          </button>
        </div>
      </div>
    </div>
  );
}
